import React from "react";
import "./AdminApplicationCrossChecks.css";
import useAdminHttp from "../../../hooks/use-admin-http";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

// A cross document check spans several document types. PASSED and SKIPPED
// come straight from the status; a FAILED check reads according to the
// severity of its rule so a warning is not mistaken for a hard rejection.
export const crossCheckKind = (record) => {
  if (record.status === "PASSED") return "passed";
  if (record.status === "SKIPPED") return "skipped";
  const severity = record.cross_document_check?.severity;
  if (severity === "WARNING") return "flagged";
  if (severity === "NOTE") return "note";
  return "failed";
};

const KIND_LABELS = {
  passed: "Passed",
  skipped: "Skipped",
  flagged: "Warning",
  note: "Note",
  failed: "Failed",
};

const KindIcon = ({ kind }) => {
  if (kind === "passed") return <CheckIcon />;
  if (kind === "skipped") return <RemoveIcon />;
  if (kind === "flagged") return <WarningAmberOutlinedIcon />;
  if (kind === "note") return <InfoOutlinedIcon />;
  return <CloseIcon />;
};

export const crossCheckCounts = (records) =>
  (records || []).reduce(
    (counts, record) => {
      counts[crossCheckKind(record)] += 1;
      counts.total += 1;
      return counts;
    },
    { passed: 0, skipped: 0, flagged: 0, note: 0, failed: 0, total: 0 }
  );

// The documents a check spans, each paired with the uploads that were
// actually present for it, so a skipped check shows which one is missing.
const documentsForRecord = (record) => {
  const uploads = record.uploads || [];
  return (record.cross_document_check?.application_documents || []).map(
    (doc) => ({
      ...doc,
      uploads: uploads
        .filter((u) => u.document_id === doc.id || u.document_slug === doc.slug)
        .sort((a, b) => (a.number ?? 1) - (b.number ?? 1)),
    })
  );
};

const AdminApplicationCrossChecks = ({ records, applicationId, onRefetch }) => {
  const counts = crossCheckCounts(records);
  const ranCount = counts.total - counts.skipped;
  const hasIssues = counts.failed + counts.flagged + counts.note > 0;

  // Re-runs every cross document check for this application against the
  // documents currently uploaded, then refetches so the new results show.
  const {
    isLoading: rerunning,
    error: rerunError,
    sendRequest: sendRerunRequest,
  } = useAdminHttp();
  const rerunChecks = () => {
    if (rerunning) return;
    sendRerunRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/cross-document-checks/rerun`,
        method: "POST",
      },
      () => {
        if (onRefetch) onRefetch();
      }
    );
  };

  const rerunButton = (
    <button
      type="button"
      className="wlac-rerun"
      onClick={rerunChecks}
      disabled={rerunning}
    >
      <ReplayOutlinedIcon
        className={rerunning ? "wlac-rerun-icon--spinning" : undefined}
      />
      {rerunning ? "Running checks..." : "Re-run checks"}
    </button>
  );

  const rerunErrorLine = rerunError && (
    <div className="wlac-rerun-error">
      {rerunError}{" "}
      <button type="button" className="wlac-rerun-retry" onClick={rerunChecks}>
        Try again
      </button>
    </div>
  );

  if (counts.total === 0) {
    return (
      <div className="wlac">
        <div className="wlac-empty">
          <CompareArrowsOutlinedIcon />
          <p>
            No cross document checks have run for this application yet. They
            run once the documents they span have been uploaded.
          </p>
          {rerunButton}
          {rerunErrorLine}
        </div>
      </div>
    );
  }

  return (
    <div className="wlac">
      <div className="wlac-section">
        <CompareArrowsOutlinedIcon />
        <span className="wlac-section-title">
          Checks across multiple documents
        </span>
        <span className={`wlac-count${hasIssues ? " wlac-count--issues" : ""}`}>
          {counts.passed}/{ranCount} passed
        </span>
        {counts.skipped > 0 && (
          <span className="wlac-count wlac-count--skipped">
            {counts.skipped} skipped
          </span>
        )}
        <span className="wlac-section-actions">{rerunButton}</span>
      </div>
      {rerunErrorLine}

      <div className="wlac-rules">
        {records.map((record) => {
          const kind = crossCheckKind(record);
          const check = record.cross_document_check || {};
          const documents = documentsForRecord(record);
          return (
            <div className={`wlac-rule wlac-rule--${kind}`} key={record.id}>
              <span className={`wlac-rule-icon wlac-rule-icon--${kind}`}>
                <KindIcon kind={kind} />
              </span>
              <div className="wlac-rule-body">
                <div className="wlac-rule-title">
                  <span className="wlac-rule-name">
                    {check.title || "Cross document check"}
                  </span>
                  <span className={`wlac-rule-badge wlac-rule-badge--${kind}`}>
                    {KIND_LABELS[kind]}
                  </span>
                  {check.severity && kind !== "passed" && kind !== "skipped" && (
                    <span className="wlac-rule-severity">
                      {check.severity.toLowerCase()}
                    </span>
                  )}
                </div>
                {check.description && (
                  <p className="wlac-rule-req">{check.description}</p>
                )}
                {kind !== "passed" && record.details && (
                  <p className="wlac-rule-details">{record.details}</p>
                )}
                {documents.length > 0 && (
                  <div className="wlac-docs">
                    {documents.map((doc) => (
                      <div className="wlac-doc" key={doc.id || doc.slug}>
                        <span className="wlac-doc-name">{doc.name}</span>
                        {doc.uploads.length === 0 ? (
                          <span className="wlac-file wlac-file--missing">
                            Not uploaded
                          </span>
                        ) : (
                          doc.uploads.map((upload) => (
                            <span
                              className="wlac-file"
                              key={upload.id}
                              title={upload.filename}
                            >
                              <AttachFileOutlinedIcon />
                              {upload.filename || upload.document_slug}
                            </span>
                          ))
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminApplicationCrossChecks;
